import type { MaxPopupMenu } from '@maxgraph/core';
import type { Store } from '@/graph-store';
import type { ToastCallbacks } from './ContextMenuService';

import { isLatestMaterial } from '@/graph-store/actions/transferHelpers';
import {
  isRootMaterial,
  isLeafMaterial,
  getLinkCandidates,
  getMergeCandidates,
  isReminderNode,
} from '@/graph/domain/topology';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';

type TransferInsertRole = 'source' | 'destination';

interface TransferInsertContext {
  transferOpId: string;
  role: TransferInsertRole;
}

function uniqueOrdered(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function getOrderedBatchSourceCandidates(materialId: string, store: Store): string[] {
  const selectedIds = store.selection?.cellIds ?? [];
  const selectedMaterialIds = selectedIds.filter((id) => Boolean(store.graphData.materialNodes[id]));
  if (!selectedMaterialIds.includes(materialId)) {
    selectedMaterialIds.push(materialId);
  }
  return uniqueOrdered(selectedMaterialIds);
}

function getOrderedDeleteMaterialCandidates(materialId: string, store: Store): string[] {
  const selectedIds = store.selection?.cellIds ?? [];
  const selectedMaterialIds = selectedIds.filter((id) => Boolean(store.graphData.materialNodes[id]));
  if (!selectedMaterialIds.includes(materialId)) {
    selectedMaterialIds.push(materialId);
  }
  return uniqueOrdered(selectedMaterialIds);
}

function getTransferInsertContext(materialId: string, store: Store): TransferInsertContext | null {
  for (const edge of Object.values(store.graphData.flowEdges)) {
    if (edge.sourceId !== materialId) continue;

    const op = store.graphData.operationNodes[edge.targetId];
    if (!op || op.archetype !== 'TRANSFER' || !op.transferRow) continue;

    if (op.transferRow.sourceMaterialId === materialId) {
      return { transferOpId: op.id!, role: 'source' };
    }

    if (op.transferRow.destinationMaterialId === materialId) {
      return { transferOpId: op.id!, role: 'destination' };
    }
  }

  return null;
}

export function buildMaterialMenu(
  menu: MaxPopupMenu,
  materialId: string,
  store: Store,
  toast?: ToastCallbacks,
): void {
  const isLocked = store.ui.isProtocolLocked || store.ui.isLifecycleLocked || store.ui.isArchived || store.ui.isArchivalInProgress || store.ui.runsExist || store.ui.canEditContent === false;
  const blockedState = {
    isProtocolLocked: store.ui.isProtocolLocked,
    isLifecycleLocked: store.ui.isLifecycleLocked,
    isArchived: store.ui.isArchived,
    isArchivalInProgress: store.ui.isArchivalInProgress,
    runsExist: store.ui.runsExist,
    canEditContent: store.ui.canEditContent,
  };

  const addStyledItem = (
    label: string,
    iconPath: string,
    onClick: () => void,
    rowClass?: string,
  ) => {
    const row = menu.addItem(label, iconPath, onClick) as unknown as HTMLElement | undefined;
    row?.classList.add('material-menu-item');
    if (rowClass) {
      row?.classList.add(rowClass);
    }
  };

  if (isLatestMaterial(materialId, store.graphData.flowEdges)) {
    // Latest material (no downstream) → Add Step
    addStyledItem('Add Step', '/icon/plus-circle.svg', () => {
      if (isLocked) {
        toast?.showWarning(getEditBlockedMessage(blockedState));
        return;
      }

      const batchCandidates = getOrderedBatchSourceCandidates(materialId, store);
      const latestMaterialIds = batchCandidates.filter((id) =>
        isLatestMaterial(id, store.graphData.flowEdges),
      );
      const filteredOutCount = batchCandidates.length - latestMaterialIds.length;

      if (filteredOutCount > 0) {
        toast?.showWarning(
          `${filteredOutCount} selected item${filteredOutCount > 1 ? 's were' : ' was'} ignored because only latest materials can be used for Add Step.`,
        );
      }

      if (latestMaterialIds.length === 0) {
        toast?.showWarning('No valid latest materials found in selection for Add Step.');
        return;
      }

      const [firstSourceMaterialId] = latestMaterialIds;
      store.openModal({
        kind: 'add-step',
        payload: {
          sourceMaterialId: firstSourceMaterialId,
          sourceMaterialIds: latestMaterialIds.length > 1 ? latestMaterialIds : undefined,
          archetypes: ['TRANSFORM', 'MEASURE', 'TRANSFER', 'COMPLEX'],
        },
      });
    }, 'material-menu-item--primary');
  } else {
    // Intermediate material (has downstream consumers) → Insert Step
    addStyledItem('Insert Step', '/icon/plus-circle.svg', () => {
      if (isLocked) {
        toast?.showWarning(getEditBlockedMessage(blockedState));
        return;
      }

      const transferContext = getTransferInsertContext(materialId, store);
      if (transferContext) {
        store.openModal({
          kind: 'transfer-insert-position',
          payload: {
            anchorMaterialId: materialId,
            transferOpId: transferContext.transferOpId,
            role: transferContext.role,
          },
        });
        return;
      }

      store.openModal({
        kind: 'insert-step',
        payload: { anchorMaterialId: materialId },
      });
    }, 'material-menu-item--primary');
  }

  const isRoot = isRootMaterial(materialId, store.graphData.flowEdges);

  if (isRoot) {
    addStyledItem('Modify Material', '/icon/modify.svg', () => {
      if (isLocked) {
        toast?.showWarning(getEditBlockedMessage(blockedState));
        return;
      }
      store.openModal({
        kind: 'edit-material',
        payload: { materialId },
      });
    }, 'material-menu-item--neutral');
  }

  // Link option: available only on endpoint materials (root or leaf)
  // BUT NOT on reminder nodes (they should not be link sources)
  const isLeaf = isLeafMaterial(materialId, store.graphData.flowEdges);
  const isReminder = isReminderNode(materialId, store.graphData.flowEdges, store.graphData.operationNodes, store.graphData.materialNodes);
  if ((isRoot || isLeaf) && !isReminder && !isLocked) {
    const candidates = getLinkCandidates(materialId, store.graphData);
    if (candidates.length > 0) {
      menu.addItem('Link', '/icon/link.svg', () => {
        store.openModal({
          kind: 'link-material',
          payload: { sourceMaterialId: materialId },
        });
      });
    }
  }

  // Merge option: available on endpoint materials (root or leaf) when merge candidates exist
  if ((isRoot || isLeaf) && !isReminder && !isLocked) {
    const contextType = isRoot ? 'ROOT' : 'LEAF';
    const mergeCandidates = getMergeCandidates(materialId, contextType, store.graphData);
    if (mergeCandidates.length > 0) {
      menu.addItem('Merge', '/icon/merge.svg', () => {
        store.openModal({
          kind: 'merge-material',
          payload: {
            sourceMaterialId: materialId,
            contextType,
          },
        });
      });
    }
  }

  const deleteCandidates = getOrderedDeleteMaterialCandidates(materialId, store);
  const isMultiDelete = deleteCandidates.length > 1;

  addStyledItem(isMultiDelete ? 'Delete Materials' : 'Delete Material', '/icon/delete.svg', () => {

    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }

    if (isMultiDelete) {
      store.openModal({
        kind: 'confirm-delete',
        payload: { kind: 'material', materialIds: deleteCandidates, materialId: deleteCandidates[0] },
      });
      return;
    }

    store.openModal({
      kind: 'confirm-delete',
      payload: { kind: 'material', materialId },
    });
  }, 'material-menu-item--danger');
}
